import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  Skeleton,
  Alert,
  TablePagination,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { toast } from 'react-hot-toast'
import { useRentals, useRentalDetails } from '../../hooks/useRentals'
import { rentalsAPI } from '../../services/api'
import { formatDate, formatNumber } from '../../utils/formatters'

// Helper function for credits display
const formatCredits = (credits) => `${credits} Kredi`

const RentalManagement = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRental, setSelectedRental] = useState(null)

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [refundOnCancel, setRefundOnCancel] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('')

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(0) // Reset to first page on search
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const { data, isLoading, error, refetch } = useRentals({
    page: page + 1,
    limit: rowsPerPage,
    search: debouncedSearch,
    status: statusFilter,
  })

  // Fetch rental details when details dialog is open
  const { data: rentalDetailsData, isLoading: isLoadingDetails } = useRentalDetails(
    selectedRental?.id,
    { enabled: detailsDialogOpen && !!selectedRental?.id }
  )

  const rentals = data?.rentals || []
  const pagination = data?.pagination || {}
  const stats = data?.stats || {}

  const handleMenuClick = (event, rental) => {
    setAnchorEl(event.currentTarget)
    setSelectedRental(rental)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
    setPage(0)
  }

  // Navigate to user profile
  const handleUserClick = (userId) => {
    if (userId) {
      navigate(`/users/${userId}`)
    }
  }

  // View details action
  const handleViewDetails = () => {
    setDetailsDialogOpen(true)
    handleMenuClose()
  }

  // Cancel action
  const handleCancelClick = () => {
    setCancelDialogOpen(true)
    handleMenuClose()
  }

  const handleCancelConfirm = async () => {
    if (!selectedRental) return

    setIsCancelling(true)
    try {
      const response = await rentalsAPI.cancelRental(selectedRental.id, refundOnCancel)
      toast.success(response.data.message)
      refetch()
      setCancelDialogOpen(false)
      setSelectedRental(null)
      setRefundOnCancel(false)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Kiralama iptal edilemedi')
    } finally {
      setIsCancelling(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'primary'
      case 'completed':
        return 'success'
      case 'expired':
        return 'error'
      case 'cancelled':
        return 'warning'
      case 'sms_received':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif'
      case 'completed':
        return 'Tamamlandi'
      case 'expired':
        return 'Suresi Doldu'
      case 'cancelled':
        return 'Iptal Edildi'
      case 'sms_received':
        return 'SMS Alindi'
      default:
        return status || 'Bilinmiyor'
    }
  }

  // Stats card component
  const StatsCard = ({ value, label, color, loading }) => (
    <Card>
      <CardContent>
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="80%" />
          </>
        ) : (
          <>
            <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main` }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  )

  if (error) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Kiralama Yonetimi
          </Typography>
        </Box>
        <Alert severity="error">
          Kiralama verileri yuklenirken hata olustu: {error.message}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Kiralama Yonetimi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              SMS kiralama islemlerini goruntuleyin ve yonetin
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatNumber(stats.active || 0)}
            label="Aktif Kiralama"
            color="primary"
            loading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatNumber(stats.completed || 0)}
            label="Tamamlanan"
            color="success"
            loading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatNumber(stats.expired || 0)}
            label="Suresi Dolan"
            color="error"
            loading={isLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            value={formatCredits(stats.totalCreditsSpent || 0)}
            label="Toplam Harcama"
            color="info"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Kullanici email veya telefon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">Tumu</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="completed">Tamamlandi</MenuItem>
                  <MenuItem value="sms_received">SMS Alindi</MenuItem>
                  <MenuItem value="expired">Suresi Doldu</MenuItem>
                  <MenuItem value="cancelled">Iptal Edildi</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
              >
                Gelismis Filtre
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rentals Table */}
      <Card>
        {isLoading && <LinearProgress />}
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Kiralama Listesi ({formatNumber(pagination.total || 0)})
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanici</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>Servis</TableCell>
                  <TableCell>Ulke</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Maliyet</TableCell>
                  <TableCell>Baslangic</TableCell>
                  <TableCell>SMS</TableCell>
                  <TableCell align="center">Islemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  // Loading skeleton rows
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                    </TableRow>
                  ))
                ) : rentals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        Kiralama bulunamadi
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rentals.map((rental) => (
                    <TableRow key={rental.id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' }
                          }}
                          onClick={() => handleUserClick(rental.user_id)}
                        >
                          <Typography variant="body2">
                            {rental.user_email || 'Anonim'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <PersonIcon sx={{ fontSize: 12 }} />
                            {rental.user_id?.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {rental.phone_number || '-'}
                      </TableCell>
                      <TableCell>{rental.service_name || '-'}</TableCell>
                      <TableCell>{rental.country_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(rental.status)}
                          color={getStatusColor(rental.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatCredits(rental.cost_in_credits || 0)}</TableCell>
                      <TableCell>
                        {rental.created_at ? formatDate(rental.created_at, 'dd/MM/yyyy HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        {rental.sms_content ? (
                          <Chip
                            label="SMS Var"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="SMS Yok"
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, rental)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Sayfa basina:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
            }
          />
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuList>
          <MenuItemComponent onClick={handleViewDetails}>
            <ViewIcon sx={{ mr: 1 }} />
            Detaylari Goruntulle
          </MenuItemComponent>
          <MenuItemComponent
            onClick={handleCancelClick}
            disabled={selectedRental?.status === 'cancelled'}
            sx={{ color: selectedRental?.status === 'cancelled' ? 'text.disabled' : 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} />
            Iptal Et
          </MenuItemComponent>
        </MenuList>
      </Menu>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kiralama Detaylari</DialogTitle>
        <DialogContent>
          {isLoadingDetails ? (
            <Box sx={{ py: 2 }}>
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" height={30} />
            </Box>
          ) : rentalDetailsData?.rental ? (
            <Box sx={{ py: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}><Typography color="text.secondary">ID:</Typography></Grid>
                <Grid item xs={6}><Typography sx={{ fontFamily: 'monospace' }}>{rentalDetailsData.rental.id}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">External ID:</Typography></Grid>
                <Grid item xs={6}><Typography sx={{ fontFamily: 'monospace' }}>{rentalDetailsData.rental.external_id || '-'}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Telefon:</Typography></Grid>
                <Grid item xs={6}><Typography sx={{ fontFamily: 'monospace' }}>{rentalDetailsData.rental.phone_number}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Servis:</Typography></Grid>
                <Grid item xs={6}><Typography>{rentalDetailsData.rental.service_name}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Ulke:</Typography></Grid>
                <Grid item xs={6}><Typography>{rentalDetailsData.rental.country_name}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Provider:</Typography></Grid>
                <Grid item xs={6}><Typography>{rentalDetailsData.rental.provider_name || '-'}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Durum:</Typography></Grid>
                <Grid item xs={6}>
                  <Chip
                    label={getStatusText(rentalDetailsData.rental.status)}
                    color={getStatusColor(rentalDetailsData.rental.status)}
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}><Typography color="text.secondary">Maliyet:</Typography></Grid>
                <Grid item xs={6}><Typography>{formatCredits(rentalDetailsData.rental.cost_in_credits)}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Olusturulma:</Typography></Grid>
                <Grid item xs={6}><Typography>{formatDate(rentalDetailsData.rental.created_at, 'dd/MM/yyyy HH:mm:ss')}</Typography></Grid>

                <Grid item xs={6}><Typography color="text.secondary">Bitis:</Typography></Grid>
                <Grid item xs={6}><Typography>{rentalDetailsData.rental.expires_at ? formatDate(rentalDetailsData.rental.expires_at, 'dd/MM/yyyy HH:mm:ss') : '-'}</Typography></Grid>

                {rentalDetailsData.rental.sms_content && (
                  <>
                    <Grid item xs={12}><Typography color="text.secondary">SMS Icerigi:</Typography></Grid>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                          {rentalDetailsData.rental.sms_content}
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          ) : (
            <Typography color="text.secondary">Detay bulunamadi</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Kapat</Button>
          {rentalDetailsData?.rental?.user_id && (
            <Button
              variant="contained"
              onClick={() => {
                setDetailsDialogOpen(false)
                navigate(`/users/${rentalDetailsData.rental.user_id}`)
              }}
            >
              Kullaniciya Git
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Kiralama Iptal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu kiralamayı iptal etmek istediğinizden emin misiniz?
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={refundOnCancel}
                  onChange={(e) => setRefundOnCancel(e.target.checked)}
                />
              }
              label={`Kredi iade et (${selectedRental?.cost_in_credits || 0} kredi)`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isCancelling}>
            Vazgec
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={isCancelling}
          >
            {isCancelling ? 'Iptal Ediliyor...' : 'Iptal Et'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RentalManagement
